/**
 * Extension Sync Utility
 * Handles bi-directional sync between web app and browser extension
 */

import { store } from '../store';
import { setCredentials, logout } from '../store/slices/authSlice';
import { secureStore } from './secureStore';
import { config } from '../config';

let isInitialized = false;

/**
 * Initialize extension sync listeners
 * Call this once when the app starts
 */
export const initializeExtensionSync = () => {
  if (isInitialized) {
    console.log('Extension sync already initialized');
    return;
  }

  console.log('Initializing extension sync...');

  // Listen for messages from extension via window.postMessage
  window.addEventListener('message', (event) => {
    // Only accept messages from same origin
    if (event.origin !== window.location.origin) {
      return;
    }

    const { type, payload } = event.data;

    console.log('Web app received message:', type, payload);

    switch (type) {
      case 'EXTENSION_LOGIN_SUCCESS':
        handleExtensionLogin(payload);
        break;

      case 'EXTENSION_LOGOUT_SUCCESS':
        handleExtensionLogout();
        break;

      case 'EXTENSION_WORKSPACE_CHANGE':
        handleExtensionWorkspaceChange(payload);
        break;

      default:
        // Ignore unknown message types
        break;
    }
  });

  // Listen for storage changes (when extension updates localStorage directly)
  window.addEventListener('storage', (event) => {
    if (event.key === 'persist:root' && event.newValue) {
      console.log('Web app detected localStorage change, rehydrating state...');
      // The Redux persist middleware will automatically rehydrate
      // We just need to trigger a page reload if needed
      const currentState = store.getState();
      if (!currentState.auth.isAuthenticated) {
        try {
          const persistedData = JSON.parse(event.newValue);
          const authData = JSON.parse(persistedData.auth);
          if (authData.isAuthenticated) {
            console.log('Web app: Detected authentication from extension, reloading...');
            window.location.reload();
          }
        } catch (error) {
          console.error('Error parsing storage change:', error);
        }
      }
    }
  });

  // Check if extension injected auth data on initial load
  checkInitialAuthState();

  isInitialized = true;
  console.log('Extension sync initialized successfully');
};

/**
 * Handle login from extension
 */
const handleExtensionLogin = (payload: any) => {
  console.log('Web app: Handling extension login', payload);

  try {
    // Store encryption keys if provided
    if (payload.encryptionKey && payload.symmetricKey && payload.vaultKey) {
      secureStore.setKeys(
        payload.encryptionKey,
        payload.symmetricKey,
        payload.vaultKey
      );
      console.log('Web app: Encryption keys synced from extension');
    }

    // Update Redux state
    if (payload.user) {
      const authData = {
        user: payload.user,
        isAuthenticated: true,
        role_id: payload.role_id || null,
        permissions: payload.permissions || null,
        team_id: payload.team_id || null,
        activeWorkspaceId: payload.workspaceId || payload.activeWorkspaceId || null,
        // Initialize workspaces as empty array - will be fetched after reload
        workspaces: [],
        activeWorkspace: null
      };

      store.dispatch(setCredentials(authData));
      console.log('Web app: Auth state synced from extension');

      // Reload to ensure full app reinitialization and fetch workspaces
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  } catch (error) {
    console.error('Error handling extension login:', error);
  }
};

/**
 * Handle logout from extension
 */
const handleExtensionLogout = () => {
  console.log('Web app: Handling extension logout');

  try {
    // Clear encryption keys
    secureStore.clearKeys();

    // Clear Redux state
    store.dispatch(logout());

    // Clear localStorage
    localStorage.removeItem('persist:root');
    sessionStorage.clear();

    console.log('Web app: Logged out due to extension logout');

    // Redirect to login
    window.location.href = '/login';
  } catch (error) {
    console.error('Error handling extension logout:', error);
  }
};

/**
 * Handle workspace change from extension
 */
const handleExtensionWorkspaceChange = (payload: any) => {
  console.log('Web app: Handling extension workspace change', payload);

  try {
    if (payload.workspaceId) {
      // Update workspace in state
      const currentState = store.getState();
      if (currentState.auth.activeWorkspaceId !== payload.workspaceId) {
        // Reload to fetch workspace-specific data
        window.location.reload();
      }
    }
  } catch (error) {
    console.error('Error handling extension workspace change:', error);
  }
};

/**
 * Check if extension has already injected auth state on page load
 */
const checkInitialAuthState = () => {
  console.log('Web app: Checking initial auth state from extension...');

  try {
    // Check if localStorage has auth data
    const persistKey = 'persist:root';
    const persistedData = localStorage.getItem(persistKey);

    if (persistedData) {
      const parsed = JSON.parse(persistedData);
      const authData = JSON.parse(parsed.auth);

      if (authData.isAuthenticated && !store.getState().auth.isAuthenticated) {
        console.log('Web app: Found auth data from extension, hydrating state...');
        
        // Check for encryption keys
        const encryptionKey = localStorage.getItem('encryptionKey');
        const symmetricKey = localStorage.getItem('symmetricKey');
        const vaultKey = localStorage.getItem('vaultKey');

        if (encryptionKey && symmetricKey && vaultKey) {
          secureStore.setKeys(encryptionKey, symmetricKey, vaultKey);
          console.log('Web app: Encryption keys loaded from localStorage');
        }

        // The Redux persist middleware will handle rehydration
        // Just log that we found the data
        console.log('Web app: Extension auth data found and will be rehydrated');
      }
    }
  } catch (error) {
    console.error('Error checking initial auth state:', error);
  }
};

/**
 * Notify extension of web app login
 * Called from useAuth hook after successful login
 */
export const notifyExtensionOfLogin = async (authData: any) => {
  console.log('Web app: Notifying extension of login...');

  try {
    // Method 1: Send message via chrome.runtime if available
    if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
      try {
        chrome.runtime.sendMessage(
          config.EXTENSION_ID,
          {
            type: 'WEB_LOGIN_SUCCESS',
            payload: authData
          },
          (response) => {
            if (chrome.runtime.lastError) {
              console.log('Extension message failed (extension may not be installed):', chrome.runtime.lastError.message);
            } else {
              console.log('Web app: Extension notified via chrome.runtime');
            }
          }
        );
      } catch (error) {
        console.log('Extension communication not available:', error);
      }
    }

    // Method 2: Post message to window (for content script)
    window.postMessage(
      {
        type: 'WEB_LOGIN_SUCCESS',
        payload: authData
      },
      window.location.origin
    );
    console.log('Web app: Login notification posted to window');
  } catch (error) {
    console.error('Error notifying extension of login:', error);
  }
};

/**
 * Notify extension of web app logout
 */
export const notifyExtensionOfLogout = async () => {
  console.log('Web app: Notifying extension of logout...');

  try {
    // Method 1: Send message via chrome.runtime if available
    if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
      try {
        chrome.runtime.sendMessage(
          config.EXTENSION_ID,
          {
            type: 'WEB_LOGOUT_SUCCESS'
          },
          (response) => {
            if (chrome.runtime.lastError) {
              console.log('Extension message failed:', chrome.runtime.lastError.message);
            }
          }
        );
      } catch (error) {
        console.log('Extension communication not available:', error);
      }
    }

    // Method 2: Post message to window (for content script)
    window.postMessage(
      {
        type: 'WEB_LOGOUT_SUCCESS'
      },
      window.location.origin
    );
    console.log('Web app: Logout notification posted to window');
  } catch (error) {
    console.error('Error notifying extension of logout:', error);
  }
};

/**
 * Check if extension is installed and responding
 */
export const checkExtensionAvailability = async (): Promise<boolean> => {
  try {
    if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
      return false;
    }

    return new Promise((resolve) => {
      chrome.runtime.sendMessage(
        config.EXTENSION_ID,
        { type: 'PING' },
        (response) => {
          if (chrome.runtime.lastError) {
            resolve(false);
          } else {
            resolve(true);
          }
        }
      );
    });
  } catch (error) {
    return false;
  }
};
